/**
 * Firebase Error Check Utilities
 *
 * Provides utility functions to check and classify Firebase authentication errors.
 * These functions help determine error types for proper error handling.
 */

export const getFirebaseErrorCode = (error: unknown): string | undefined => {
  if (!error || typeof error !== 'object') {
    return undefined;
  }
  const code = (error as { code?: string; errorCode?: string }).code ?? (error as { errorCode?: string }).errorCode;
  return typeof code === 'string' ? code : undefined;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return String(error ?? '');
};

/**
 * Checks if an error is related to invalid session ID
 * @param error - The error object from Firebase
 * @returns true if the error is an invalid session ID error
 */
export const isInvalidSessionIdError = (error: any): boolean => {
  const errorCode = error?.code || error?.errorCode;
  const errorMessage = error?.message || '';

  return (
    errorCode === 'auth/invalid-session-id' ||
    errorMessage.includes('invalid session_id') ||
    errorMessage.includes('invalid-session-id')
  );
};

/**
 * Checks if an error is a network-related error
 * @param error - The error object from Firebase
 * @returns true if the error is network-related or device is offline
 */
export const isNetworkError = (error: any): boolean => {
  const errorCode = error?.code || error?.errorCode;
  const errorMessage = error?.message || '';

  return (
    !navigator.onLine ||
    errorCode === 'auth/network-request-failed' ||
    errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('fetch')
  );
};

/**
 * Checks if an error indicates the token has expired
 * @param error - The error object from Firebase
 * @returns true if the error indicates token expiration
 */
export const isTokenExpiredError = (error: any): boolean => {
  const errorCode = error?.code || error?.errorCode;
  const errorMessage = error?.message || '';

  return (
    errorCode === 'auth/id-token-expired' ||
    errorCode === 'auth/argument-error' ||
    errorMessage.toLowerCase().includes('expired') ||
    errorMessage.toLowerCase().includes('expiry')
  );
};

/**
 * Checks if an error indicates the user is unauthorized
 * @param error - The error object from Firebase
 * @returns true if the error indicates unauthorized access
 */
export const isUnauthorizedError = (error: any): boolean => {
  const errorCode = error?.code || error?.errorCode;

  return (
    errorCode === 'auth/unauthorized' ||
    errorCode === 'auth/permission-denied' ||
    errorCode === 'auth/operation-not-allowed'
  );
};

const PERMANENT_AUTH_ERROR_CODES = new Set([
  'auth/user-disabled',
  'auth/user-token-expired',
  'auth/invalid-user-token',
  'auth/invalid-credential',
  'auth/user-not-found',
  'auth/operation-not-allowed',
]);

const TRANSIENT_AUTH_ERROR_CODES = new Set([
  'auth/network-request-failed',
  'auth/visibility-check-was-unavailable',
  'auth/internal-error',
  'auth/timeout',
  'auth/too-many-requests',
]);

/**
 * Permanent auth failures where the session cannot be recovered without re-login.
 */
export const isPermanentAuthError = (error: unknown): boolean => {
  const errorCode = getFirebaseErrorCode(error);
  if (errorCode && PERMANENT_AUTH_ERROR_CODES.has(errorCode)) {
    return true;
  }

  return isUnauthorizedError(error);
};

/**
 * Transient auth failures (network, timeouts, etc.) where the session should be preserved.
 */
export const isTransientAuthError = (error: unknown): boolean => {
  if (isNetworkError(error)) {
    return true;
  }

  const errorCode = getFirebaseErrorCode(error);
  if (errorCode && TRANSIENT_AUTH_ERROR_CODES.has(errorCode)) {
    return true;
  }

  const errorMessage = getErrorMessage(error).toLowerCase();
  return /timeout|aborted|failed to fetch|network error|load failed/.test(errorMessage);
};

/**
 * Classifies a Firebase error into a category
 * @param error - The error object from Firebase
 * @returns A string indicating the error category
 */
export const classifyFirebaseError = (
  error: any,
): 'invalid-session-id' | 'network' | 'token-expired' | 'unauthorized' | 'unknown' => {
  if (isInvalidSessionIdError(error)) {
    return 'invalid-session-id';
  }
  if (isNetworkError(error)) {
    return 'network';
  }
  if (isTokenExpiredError(error)) {
    return 'token-expired';
  }
  if (isUnauthorizedError(error)) {
    return 'unauthorized';
  }
  return 'unknown';
};
